import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import styles from "./catalog.module.css";

/**
 * CATALOGUE REQUESTS (the proposal queue)
 *
 * On a shared (house-owned) product a vendor may price the shelves that exist
 * and nothing else — because an approved NAME applies to every vendor on that
 * shelf, so one vendor naming it would silently change what the others are
 * selling. A proposal is how they ask for a plan, a billing period or a
 * rename, and this screen is where it is answered.
 *
 * Approving is not a rubber stamp: the reviewer can correct the wording before
 * it becomes everyone's name, and must supply a plan type when the vendor
 * could not pick one (they were asking for a plan that does not exist yet).
 * Rejecting REQUIRES a note — the vendor sees it and it is the only thing they
 * can act on; a refusal with no reason is a support ticket waiting to happen.
 *
 * APIs: GET /admin/catalog/proposals, POST .../:id/approve, POST .../:id/reject.
 */

const API = process.env.REACT_APP_SERVER_URL;

const KIND_LABEL = {
  plan: "New plan",
  interval: "New billing period",
  rename: "Rename",
};

const KIND_BLURB = {
  plan: "A plan this product does not carry. Approving creates the shelf and publishes their attached prices onto it.",
  interval:
    "A plan we already carry, at a billing period we do not. Approving creates that shelf.",
  rename:
    "A different name for an existing shelf. Approving renames it for every vendor on it.",
};

const period = (p) =>
  p.n_interval || p.interval ? `${p.n_interval ?? 1} ${p.interval || ""}`.trim() : "—";

function CatalogProposals() {
  // Reached from the Approvals queue, which is the one inbox: a catalogue
  // request is listed there with everything else and opens HERE, because the
  // decision is a different one (approving changes the shelf for every vendor
  // on it, not one vendor's prices). `?focus=` carries which row was clicked.
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focusId = Number(params.get("focus")) || null;
  // A focused row may be already decided, so open on "All" rather than show
  // "Nothing waiting" for a request the admin just clicked on.
  const [status, setStatus] = useState(focusId ? "all" : "pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [decision, setDecision] = useState(null); // { row, mode, plan_name, type_id, note }
  const [busy, setBusy] = useState(false);
  const [types, setTypes] = useState([]); // plan types of the proposal's product

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/admin/catalog/proposals?status=${encodeURIComponent(status)}`,
        { credentials: "include" },
      );
      const body = await res.json();
      if (!body.success) {
        toast.error(body.msg || "Failed to load catalogue requests");
        setRows([]);
      } else {
        setRows(body.proposals || []);
      }
    } catch {
      toast.error("Failed to load catalogue requests");
    }
    setLoading(false);
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    // Opened from the queue on one request: show that one, not the backlog.
    if (focusId) {
      const one = rows.filter((r) => Number(r.id) === focusId);
      if (one.length) return one;
    }
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.product_name, r.vendor_name, r.plan_name, r.new_name, r.current_name, r.reason]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [rows, query, focusId]);

  // The plan-type picker only matters when the vendor could not name one; the
  // types come from the product the request is against.
  const openDecision = async (row, mode) => {
    setDecision({
      row,
      mode,
      plan_name: row.kind === "rename" ? row.new_name : row.plan_name,
      type_id: row.type_id ?? "",
      n_interval: row.n_interval ?? "",
      interval: row.interval || "",
      note: "",
    });
    setTypes([]);
    if (mode === "approve" && row.kind !== "rename") {
      try {
        const res = await fetch(
          `${API}/admin/catalog/shelves?skuId=${row.sku_id}`,
          { credentials: "include" },
        );
        const body = await res.json();
        if (body.success) setTypes(body.plan_types || []);
      } catch {
        /* the picker just stays empty — type_id is optional on approve */
      }
    }
  };

  const submit = async () => {
    if (!decision) return;
    const { row, mode } = decision;
    if (mode === "reject" && !decision.note.trim()) {
      toast.error("Say why — the vendor sees this note and it is all they can act on.");
      return;
    }
    setBusy(true);
    try {
      const payload =
        mode === "approve"
          ? {
              note: decision.note.trim() || undefined,
              plan_name: decision.plan_name?.trim() || undefined,
              type_id: decision.type_id === "" ? undefined : Number(decision.type_id),
              // Only sent for plan/interval requests — a rename has no period
              // of its own, and sending one would amend the wrong thing.
              ...(decision.row.kind === "rename"
                ? {}
                : {
                    n_interval:
                      decision.n_interval === "" ? undefined : Number(decision.n_interval),
                    interval: decision.interval?.trim() || undefined,
                  }),
            }
          : { note: decision.note.trim() };
      const res = await fetch(
        `${API}/admin/catalog/proposals/${row.id}/${mode}`,
        {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );
      const body = await res.json();
      if (!body.success) {
        toast.error(body.msg || `Failed to ${mode} the request`);
        setBusy(false);
        return;
      }
      toast.success(
        mode === "approve"
          ? "Approved — the vendor has been told."
          : "Rejected — the vendor has been told, with your note.",
      );
      setDecision(null);
      await load();
    } catch {
      toast.error(`Failed to ${mode} the request`);
    }
    setBusy(false);
  };

  return (
    <div className={styles.Page}>
      <div className={styles.PageHead}>
        <div>
          {focusId && (
            <Button
              size="small"
              onClick={() => navigate("/dashboard/productapproval")}
              style={{ marginBottom: 6, paddingLeft: 0 }}
            >
              ← Back to approvals
            </Button>
          )}
          <h1 className={styles.PageTitle}>
            {focusId ? "Catalogue request" : "Catalogue requests"}
          </h1>
          <p className={styles.PageSub}>
            Vendors on a shared product can price the shelves we carry, but not
            invent or rename them — an approved name applies to everyone selling
            that plan. These are the asks.
          </p>
        </div>
        <div
          className={styles.HeadActions}
          style={focusId ? { display: "none" } : undefined}
        >
          <Select
            size="small"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
          <TextField
            size="small"
            placeholder="Search product, vendor or plan"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button variant="outlined" size="small" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className={styles.Loading}>
          <CircularProgress size={26} />
        </div>
      ) : !visible.length ? (
        <p className={styles.Empty}>
          {status === "pending"
            ? "Nothing waiting. Vendor requests for new plans, new billing periods and renames land here."
            : "Nothing matches."}
        </p>
      ) : (
        <div className={styles.List}>
          {visible.map((r) => (
            <div key={r.id} className={styles.Card}>
              <div className={styles.CardHead}>
                <div className={styles.CardTitleBlock}>
                  <div className={styles.CardTitle}>
                    {r.kind === "rename" ? (
                      <>
                        <span className={styles.Strike}>{r.current_name || "—"}</span>
                        <span className={styles.Arrow}>→</span>
                        <span>{r.new_name}</span>
                      </>
                    ) : (
                      r.plan_name || "(no name given)"
                    )}
                  </div>
                  <div className={styles.CardMeta}>
                    <strong>{r.product_name || `Product #${r.sku_id}`}</strong>
                    <span>·</span>
                    <span>{r.vendor_name || `Vendor #${r.vendor_id}`}</span>
                    <span>·</span>
                    <span>{period(r)}</span>
                    {r.region ? (
                      <>
                        <span>·</span>
                        <span>{r.region}</span>
                      </>
                    ) : null}
                    {r.currency ? (
                      <>
                        <span>·</span>
                        <span>{r.currency}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className={styles.Chips}>
                  <Chip
                    size="small"
                    label={KIND_LABEL[r.kind] || r.kind}
                    color={r.kind === "rename" ? "warning" : "primary"}
                    variant="outlined"
                  />
                  {r.has_prices ? (
                    <Chip size="small" label="Prices attached" color="success" variant="outlined" />
                  ) : null}
                  {r.status !== "pending" ? (
                    <Chip
                      size="small"
                      label={r.status}
                      color={r.status === "approved" ? "success" : "default"}
                    />
                  ) : null}
                </div>
              </div>

              <p className={styles.Blurb}>{KIND_BLURB[r.kind]}</p>

              {r.type_name || r.type_id ? (
                <p className={styles.Field}>
                  <span className={styles.FieldLabel}>Plan type</span>
                  {r.type_name || `#${r.type_id}`}
                </p>
              ) : r.kind !== "rename" ? (
                <p className={styles.FieldWarn}>
                  <span className={styles.FieldLabel}>Plan type</span>
                  The vendor could not pick one — choose it when you approve.
                </p>
              ) : null}

              {r.description ? (
                <p className={styles.Field}>
                  <span className={styles.FieldLabel}>Description</span>
                  {r.description}
                </p>
              ) : null}

              {r.reason ? (
                <p className={styles.Field}>
                  <span className={styles.FieldLabel}>Their reason</span>
                  {r.reason}
                </p>
              ) : null}

              {r.status === "pending" ? (
                <div className={styles.CardActions}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => openDecision(r, "approve")}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => openDecision(r, "reject")}
                  >
                    Reject
                  </Button>
                </div>
              ) : (
                <div className={styles.Decided}>
                  <span className={styles.FieldLabel}>
                    {r.status === "approved" ? "Approved" : "Rejected"}
                    {r.decided_at
                      ? ` ${new Date(r.decided_at).toLocaleString()}`
                      : ""}
                  </span>
                  {r.decision_note ? (
                    <span className={styles.Note}>{r.decision_note}</span>
                  ) : (
                    <span className={styles.Muted}>No note was left.</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={Boolean(decision)}
        onClose={() => (busy ? null : setDecision(null))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {decision?.mode === "approve" ? "Approve this request" : "Reject this request"}
        </DialogTitle>
        <DialogContent>
          {decision?.mode === "approve" ? (
            <>
              <p className={styles.DialogNote}>
                {decision.row.kind === "rename"
                  ? "The new name is written to the shelf and to every vendor's offer on it. No prices move."
                  : "This creates the shelf and publishes any prices the vendor attached. Correct the wording first if it needs it — it becomes everyone's name."}
              </p>
              <TextField
                fullWidth
                size="small"
                label="Plan name"
                value={decision.plan_name || ""}
                onChange={(e) =>
                  setDecision((d) => ({ ...d, plan_name: e.target.value }))
                }
                disabled={decision.row.kind === "rename"}
                helperText={
                  decision.row.kind === "rename"
                    ? "A rename approves the name they asked for, or you reject it and say why."
                    : "Correct the wording if needed."
                }
              />
              {decision.row.kind !== "rename" ? (
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={decision.type_id === null ? "" : decision.type_id}
                  onChange={(e) =>
                    setDecision((d) => ({ ...d, type_id: e.target.value }))
                  }
                  className={styles.DialogField}
                >
                  <MenuItem value="">
                    {decision.row.type_id
                      ? "Keep the type they chose"
                      : "No plan type — pick one"}
                  </MenuItem>
                  {types.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : null}
              {/* THE PERIOD IS AMENDABLE TOO. A vendor asking for "Yearly" and
                  typing 12 months when the catalogue carries 1 year creates a
                  second shelf for the same thing — the duplicate-shelves
                  screen then exists to clean up what could have been fixed in
                  one field here. */}
              {decision.row.kind !== "rename" ? (
                <div className={styles.DialogRow}>
                  <TextField
                    size="small"
                    type="number"
                    label="Every"
                    value={decision.n_interval}
                    onChange={(e) =>
                      setDecision((d) => ({ ...d, n_interval: e.target.value }))
                    }
                    inputProps={{ min: 1 }}
                  />
                  <Select
                    size="small"
                    value={decision.interval || ""}
                    onChange={(e) =>
                      setDecision((d) => ({ ...d, interval: e.target.value }))
                    }
                    displayEmpty
                  >
                    <MenuItem value="">Keep “{decision.row.interval || "—"}”</MenuItem>
                    {["day", "week", "month", "quarter", "year", "lifetime"].map((u) => (
                      <MenuItem key={u} value={u}>
                        {u}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              ) : null}
              <TextField
                fullWidth
                size="small"
                label="Note to the vendor (optional)"
                value={decision.note}
                onChange={(e) => setDecision((d) => ({ ...d, note: e.target.value }))}
                multiline
                minRows={2}
                className={styles.DialogField}
              />
            </>
          ) : (
            <>
              <p className={styles.DialogNote}>
                The vendor sees this note and it is the only thing they can act
                on. Nothing is published — the prices they attached stay on the
                request, so they can read your reason beside the figures you
                rejected and send it back corrected.
              </p>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Why (required)"
                value={decision?.note || ""}
                onChange={(e) => setDecision((d) => ({ ...d, note: e.target.value }))}
                multiline
                minRows={3}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecision(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={decision?.mode === "reject" ? "error" : "primary"}
            onClick={submit}
            disabled={busy || (decision?.mode === "reject" && !decision?.note?.trim())}
          >
            {busy
              ? "Working…"
              : decision?.mode === "approve"
                ? "Approve"
                : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default CatalogProposals;
